import type { SubscriptionStatus } from '@structural-climbing/ui'
import { useAuth, useCachedFetch } from '@structural-climbing/auth'

export interface MyGym {
  id: string
  name: string
  slug: string
  /** Absolute API-worker URL, already cache-busted. Null until a gym uploads one. */
  image_url: string | null
  /** The cover photo, for the cover tiles in "Sălile tale". Null until uploaded. */
  cover_url?: string | null
}

export interface MembershipEntry {
  gym: MyGym
  subscription: { effective_status: SubscriptionStatus; is_checked_in: boolean } | null
}

/**
 * Set when this login is a child's own account, invited by a parent.
 *
 * Such an account has no membership of its own: the gyms listed are the
 * parent's, and it gets the climbing screens only. Null for everyone else.
 */
export interface ChildAccount {
  /** Gyms where the child's own plan or check-in lets them log routes. */
  canLogGymIds: string[]
}

interface MembershipsData {
  memberships: MembershipEntry[]
  childAccount: ChildAccount | null
  pendingChildInvite: boolean
}

// One stable reference, so selectors memoised on `memberships` do not recompute
// on every render while the list is still loading.
const EMPTY: MembershipEntry[] = []

/** The one cache key every membership-derived screen reads through. */
export const MY_MEMBERSHIPS_KEY = 'my-gyms'

/**
 * Every gym this member belongs to, with the subscription that gets them in.
 *
 * `/my-gym-memberships` was being fetched three times over — once cached here
 * for the switcher, and twice uncached on mount (the gym layout and the redirect
 * screen). The uncached copies were the problem: the layout reads
 * `hasActiveSubscription` out of this response to decide whether the member may
 * log routes, and a plan cancelled at the desk never reached it, so a cancelled
 * member kept logging until they force-reloaded.
 *
 * One key, so `invalidateCachedFetch('my-gyms')` — which the realtime listener
 * already fires — corrects all of them at once.
 */
export function useMyMemberships(): {
  memberships: MembershipEntry[]
  childAccount: ChildAccount | null
  /**
   * This account signed up with an address a parent's invite is waiting for,
   * and has not claimed it. It belongs on the invite, not on the join form.
   */
  pendingChildInvite: boolean
  loading: boolean
  refresh: () => Promise<void>
} {
  const { isAuthenticated, getAccessTokenSilently } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const { data, loading, refresh } = useCachedFetch<MembershipsData>(
    isAuthenticated ? MY_MEMBERSHIPS_KEY : null,
    async () => {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/my-gym-memberships`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error('Failed to load gyms')
      const body = (await res.json()) as {
        memberships: MembershipEntry[]
        child_account?: { can_log_gym_ids?: string[] } | null
        pending_child_invite?: boolean
      }
      return {
        memberships: body.memberships.filter((m) => m.gym),
        childAccount: body.child_account
          ? { canLogGymIds: body.child_account.can_log_gym_ids ?? [] }
          : null,
        pendingChildInvite: body.pending_child_invite === true,
      }
    },
  )

  return {
    memberships: data?.memberships ?? EMPTY,
    childAccount: data?.childAccount ?? null,
    pendingChildInvite: data?.pendingChildInvite ?? false,
    loading,
    refresh,
  }
}
